import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { API, apiErrorMessage } from "../../../../helpers/API/amplifyApi";
import { problematikat as problematikatAction } from "../../../../store/actions";
import types from "../../../../store/types/types";
import {
  notificationUserToAdmin,
  notificationAdminToOnlyOneUser,
} from "../../../../utils/sendNotification";
import { eventTracker } from "../../../../utils/eventTracker";
import { useAuth } from "../../../../authentication/authHelpers";
import { message } from "antd";
import { getTicketMessages, toMessagePreview } from "../utils/ticketStatus";
import { isEmptyChatHtml } from "../utils/chatContent";
import { MAX_CHAT_BODY_CHARS } from "../utils/compressChatImage";

export function useTicketMessages() {
  const dispatch = useDispatch();
  const { user, employeeId } = useAuth();
  const { activeEmployees } = useSelector((state) => state.employeesList);
  const [sending, setSending] = useState(false);

  const notifyOnReply = useCallback(
    (ticket, authorRole, preview) => {
      const category = ticket?.ticketCategory || "problematikë";
      const common = {
        notificationUrl: `/problematikat?ticket=${ticket.ticketId}`,
        notificationType: "support_ticket",
        entityId: ticket.ticketId,
      };
      if (authorRole === "employee") {
        notificationUserToAdmin(
          {
            notificationTitle: "Përgjigje e re në Support",
            notificationBody: `${ticket?.createdBy || "Punonjësi"} u përgjigj: ${preview}`,
            ...common,
          },
          user?.allUsers?.Items
        );
        return;
      }

      const employee = activeEmployees?.find(
        (el) => el?.employeeId === ticket.employeeId
      );
      const identityMatch = user?.allUsers?.Items?.find(
        (el) => el?.userSub === employee?.userSub
      );
      if (!identityMatch?.identityId) return;

      notificationAdminToOnlyOneUser(
        {
          notificationTitle: "Përgjigje nga Support",
          notificationBody: `Përgjigje e re për: ${category}`,
          ...common,
        },
        { identityId: identityMatch.identityId }
      );
    },
    [activeEmployees, user]
  );

  const sendReply = useCallback(
    async (ticket, body, authorRole) => {
      if (!ticket?.ticketId || isEmptyChatHtml(body)) return null;

      if (String(body).length > MAX_CHAT_BODY_CHARS) {
        message.error(
          "Mesazhi (me foto) është shumë i madh. Hiqni foton ose zgjidhni një më të vogël."
        );
        return null;
      }

      setSending(true);
      const optimisticId = `tmp-${Date.now()}`;
      const optimisticMessage = {
        messageId: optimisticId,
        authorId: authorRole === "employee" ? employeeId : user?.identityId,
        authorName: `${user?.given_name || ""} ${user?.family_name || ""}`.trim(),
        authorRole,
        body,
        createdAt: Date.now(),
        attachments: [],
      };

      const priorMessages = getTicketMessages(ticket).filter(
        (m) => !String(m.messageId || "").startsWith("legacy-")
      );
      // Keep real messages; if only a legacy synthetic bubble exists, seed it
      // so list_append on the server and optimistic UI stay aligned.
      const baseMessages =
        Array.isArray(ticket.messages) && ticket.messages.length > 0
          ? ticket.messages
          : priorMessages.length > 0
            ? priorMessages
            : getTicketMessages(ticket);

      const optimisticTicket = {
        ...ticket,
        messages: [...baseMessages, optimisticMessage],
        lastMessageAt: optimisticMessage.createdAt,
        lastMessagePreview: toMessagePreview(body),
        ticketStatus:
          authorRole === "admin" ? "WaitingOnEmployee" : "InProgress",
      };
      dispatch(problematikatAction(optimisticTicket));

      try {
        const resp = await API.post(
          "tickets",
          `/tickets/${ticket.ticketId}/messages`,
          { body: { body } }
        );
        const updated = resp?.ticket || {
          ...ticket,
          messages: [...baseMessages, resp?.message].filter(Boolean),
          lastMessageAt: resp?.message?.createdAt,
          lastMessagePreview: toMessagePreview(resp?.message?.body || body),
        };
        dispatch(problematikatAction(updated));
        eventTracker("Support", "click", "Reply sent");
        notifyOnReply(updated, authorRole, toMessagePreview(body));
        return updated;
      } catch (err) {
        console.error("sendReply failed", err);
        dispatch(problematikatAction(ticket));
        const status = err?.response?.status || err?.status;
        const friendly =
          status === 413 || status === 400
            ? "Fotoja/mesazhi është shumë i madh për dërgim. Provoni një foto më të vogël."
            : apiErrorMessage(err, "Mesazhi nuk u dërgua. Provoni përsëri.");
        message.error(friendly);
        return null;
      } finally {
        setSending(false);
      }
    },
    [dispatch, employeeId, notifyOnReply, user]
  );

  const updateTicket = useCallback(
    async (ticket, patch) => {
      if (!ticket?.ticketId) return null;
      await API.put("tickets", `/tickets/${ticket.ticketId}`, {
        body: patch,
      });
      const updated = { ...ticket, ...patch, ticketId: ticket.ticketId };
      dispatch(problematikatAction(updated));
      if (patch.ticketStatus === "Completed") {
        eventTracker("Support", "click", "Ticket completed");
        const employee = activeEmployees?.find(
          (el) => el?.employeeId === ticket.employeeId
        );
        const identityMatch = user?.allUsers?.Items?.find(
          (el) => el?.userSub === employee?.userSub
        );
        if (identityMatch?.identityId) {
          notificationAdminToOnlyOneUser(
            {
              notificationTitle: "U Zgjidh Problematika",
              notificationBody: `Problematika "${ticket.ticketCategory}" u shënua si e zgjidhur`,
              notificationUrl: `/problematikat?ticket=${ticket.ticketId}`,
            },
            { identityId: identityMatch.identityId }
          );
        }
      } else {
        eventTracker("Support", "click", "Ticket status updated");
      }
      return updated;
    },
    [activeEmployees, dispatch, user]
  );

  const deleteTicket = useCallback(
    async (ticket) => {
      if (!ticket?.ticketId) return;
      await API.del("tickets", `/tickets/${ticket.ticketId}`);
      dispatch(problematikatAction(ticket, types.REMOVE_PROBLEMS));
      eventTracker("Support", "click", "Ticket deleted");
    },
    [dispatch]
  );

  return { sendReply, updateTicket, deleteTicket, sending };
}
